/**
 * Panel registry types — the Meridian semantic layer over
 * dockview-react's opaque ``SerializedDockview`` blob.
 *
 * Per ADR-024 §2 and the chrome-spec.md 2026-05-26 amendment:
 * the dockable primary surfaces plus the adjunct Entity Details
 * panel. (The retired ``allow-list`` panel — the allow-list capability
 * was retired 2026-05-22 — was dropped from this inventory in the
 * PR5a cleanup; the ``detections`` + ``alerts`` surfaces were added in
 * the ADR-032 / ADR-029 cascade — PR5a inc 2 / inc 3, REQ-UI-061 /
 * REQ-UI-062; the data-plane ``events`` surface they supersede was
 * removed in PR6.) Each panel declares its role gating + display name;
 * the registry filters by the current user's roles per
 * REQ-AUTH-024..027 + REQ-UI-047.
 *
 * Cross-references:
 *   - ADR-024 §2 (dockable surfaces inventory).
 *   - ADR-024 §6 (two-tier ownership: Meridian semantic layer +
 *     opaque dockview_state).
 *   - REQ-UI-047 (role-restricted surfaces filtered from the Add
 *     panel menu).
 *   - REQ-UI-077 (the built-in default Profile's layout — DEFAULT_LAYOUT).
 *   - docs/attic/mvp/streams/C-multi-pane.md.
 */
import type { Role } from "@/auth/session-types";

/**
 * The primary dockable surfaces per ADR-024 §2 plus the adjunct
 * ``entity-details`` panel. (The retired ``allow-list`` surface was
 * dropped in the PR5a cleanup; ``detections`` + ``alerts`` were added in
 * the ADR-032 / ADR-029 cascade — PR5a inc 2 / inc 3, REQ-UI-061 /
 * REQ-UI-062 — superseding the data-plane ``events`` surface removed in
 * PR6.)
 *
 * Naming convention: kebab-case so the ID survives serialization
 * into ``panel_layout.panels[].id`` and ``dockview_state``
 * unchanged. Used as the lookup key in both the React panel-
 * component registry and the :data:`DEFAULT_LAYOUT` grid definition.
 */
export type PanelId =
  | "map"
  | "query"
  | "platforms"
  | "detections"
  | "tracks"
  | "alerts"
  | "audit-log"
  | "onboarding"
  | "user-management"
  | "system-status"
  | "entity-details";

/**
 * Every PanelId as an iteration-friendly array. Order is the
 * canonical ADR-024 §2 inventory order.
 */
export const ALL_PANEL_IDS: readonly PanelId[] = [
  "map",
  "query",
  "platforms",
  "detections",
  "tracks",
  "alerts",
  "audit-log",
  "onboarding",
  "user-management",
  "system-status",
  "entity-details",
] as const;

/**
 * Panel display titles — the tab-header text + the Add panel
 * menu label per Flow P's layout submenu.
 */
export const PANEL_TITLES: Record<PanelId, string> = {
  map: "Map",
  query: "Query",
  platforms: "Platforms",
  detections: "Detections",
  tracks: "Tracks",
  alerts: "Alerts",
  "audit-log": "Audit Log",
  onboarding: "Onboarding",
  "user-management": "User Management",
  "system-status": "System",
  "entity-details": "Selection Details",
};

/**
 * Per-panel role gating per ADR-024 §2.
 *
 * - ``map`` / ``platforms`` / ``detections`` / ``alerts`` /
 *   ``entity-details``: visible to every authenticated role. (Per-alert
 *   *dismissal* is operator/admin-only — gated in the panel, not by panel
 *   visibility — per ADR-029 §7.)
 * - ``query``: every authenticated role, read-only (ADR-038 §Decision 4 /
 *   REQ-AUTH-075 — a deliberate broadening of REQ-AUTH-024's operator
 *   map-only scope, landing consistently with the ADR-036 / ADR-037 D5
 *   all-roles-read direction). Disclosure is bounded at the BFF rather
 *   than by hiding the panel: the catalog is rendered per role and the
 *   query compiler re-validates every attribute against the caller's
 *   visible set, so privileged attributes are unreachable even by a
 *   crafted request (ADR-038 §Decision 4 / D9).
 * - ``audit-log``: ``admin`` + ``support_engineer`` (architect ruling
 *   2026-08-01, widening the 2026-06-08 §6 admin-only ruling —
 *   gap-report GAP-55). REQ-LOG-021 and REQ-LOG-022 had always granted
 *   both roles read access to ``logs.audit_events`` including sensitive
 *   event types; the narrower gate contradicted them and left the
 *   incident-response role unable to read the log it works from.
 *   Broadening further to ``operator`` / ``analyst`` remains post-MVP
 *   and is a *different* change — those roles read through the
 *   REQ-LOG-032 per-role unsensitive projection rather than the base
 *   table, gated on the sensitive-event-type list being populated
 *   (deliberately empty at MVP per REQ-LOG-023).
 * - ``onboarding`` / ``user-management``: admin only per
 *   REQ-AUTH-024.
 * - ``system-status``: **every authenticated role**, read-only, per ADR-036 §D-7 /
 *   REQ-PEER-042 (2026-07-25) — broadening the original REQ-STATUS-029
 *   support_engineer+admin gate. The display now carries the Peer Manager
 *   interfaces, and those are the flows leaving the system: an operator who
 *   cannot see them cannot notice one running that should not be. Mutation
 *   (aggregator disable, and the peer management affordances) stays
 *   ``admin`` / ``support_engineer`` — non-privileged roles see the surface with
 *   mutation controls **absent rather than disabled** (Flow L §1).
 *
 * Saved layouts that reference role-restricted panels for which
 * the current role no longer matches are silently elided at
 * restore time per REQ-UI-047.
 */
export const PANEL_ROLES: Record<PanelId, readonly Role[]> = {
  map: ["operator", "analyst", "admin", "support_engineer"],
  query: ["operator", "analyst", "admin", "support_engineer"],
  platforms: ["operator", "analyst", "admin", "support_engineer"],
  detections: ["operator", "analyst", "admin", "support_engineer"],
  // ADR-041 (REQ-UI-170): the Tracks panel carries the Detections panel's gate.
  tracks: ["operator", "analyst", "admin", "support_engineer"],
  alerts: ["operator", "analyst", "admin", "support_engineer"],
  "audit-log": ["admin", "support_engineer"],
  onboarding: ["admin"],
  "user-management": ["admin"],
  "system-status": ["operator", "analyst", "admin", "support_engineer"],
  "entity-details": ["operator", "analyst", "admin", "support_engineer"],
};

/**
 * Filter the panel inventory by the user's roles. Returns the
 * subset of ``ALL_PANEL_IDS`` the user is allowed to see in the
 * Panels menu + the layout restore role-gate.
 */
export function getVisiblePanels(
  hasAnyRole: (roles: readonly Role[]) => boolean,
): readonly PanelId[] {
  return ALL_PANEL_IDS.filter((id) => hasAnyRole(PANEL_ROLES[id]));
}

/**
 * The current ``panel_layout.layout_schema_version`` per
 * ADR-024 §6. Bumped when the Meridian semantic layer shape
 * changes incompatibly; the BFF migrates older shapes forward
 * at read time per REQ-UI-054.
 */
export const LAYOUT_SCHEMA_VERSION = 1;
